import { toast } from './toast.js';
import { auth } from './auth.js';

/**
 * fetch wrapper for the JSON API.
 *
 * - Endpoints passed as bare names are mapped to zapd's base path.
 * - Always parses JSON.
 * - Auto-shows the `toast` field from the response (if present).
 * - Adds Authorization: Bearer <token> from the in-memory access token.
 * - On 401, tries one refresh + retry. If refresh fails, the user is
 *   redirected to /login by auth.refresh's caller.
 */
export async function api(endpoint, opts = {}) {
  return apiCall(endpoint, opts, false);
}

async function apiCall(endpoint, opts, isRetry) {
  const final = {
    credentials: 'include',
    ...opts,
  };

  if (final.body && typeof final.body === 'object' && !(final.body instanceof FormData)) {
    final.headers = { 'Content-Type': 'application/json', ...final.headers };
    final.body = JSON.stringify(final.body);
  }

  // Always attach the access token if we have one
  const token = auth.getToken();
  if (token) {
    final.headers = { Authorization: 'Bearer ' + token, ...final.headers };
  }

  // Absolute paths pass through; bare endpoint names use zapd directly.
  let url;
  if (endpoint.startsWith('/') || endpoint.startsWith('http')) {
    url = endpoint;
  } else {
    url = '/api-zapd/' + endpoint;
  }

  // opts.silent: caller handles failures itself (e.g. optional startup seeds
  // like the hls-live badge) — no user-facing error toast, the rejection still
  // propagates. Strip it from the fetch init (fetch ignores unknown keys, but
  // keep the surface clean).
  const silent = !!final.silent;
  delete final.silent;

  let r, json;
  try {
    r = await fetch(url, final);
  } catch (e) {
    if (!silent) toast.error('Netzwerk-Fehler');
    throw e;
  }

  // 401 → refresh once and retry, but only for "regular" calls (not the
  // refresh endpoint itself, to avoid loops).
  if (r.status === 401 && !isRetry && !endpoint.startsWith('auth/')) {
    try {
      await auth.refresh();
    } catch {
      window.location =
        '/login?next=' + encodeURIComponent(window.location.pathname + window.location.search);
      throw new Error('not authenticated');
    }
    return apiCall(endpoint, opts, true);
  }

  try {
    json = await r.json();
  } catch (e) {
    if (!silent) toast.error('Ungültige Antwort');
    throw e;
  }

  if (json.toast && !silent) toast[json.toast.type ?? 'info'](json.toast.msg);

  if (!json.ok) {
    // json.error may be a structured {code,message} (e.g. the NOT_MIGRATED
    // stubs) — surface the message text, never the object.
    const errMsg =
      (json.error && (typeof json.error === 'string' ? json.error : json.error.message)) ||
      json.err ||
      'Aktion fehlgeschlagen';
    // Contract 03 §4 D7: a 403 with reason "download" means the account has no
    // download grant — the area behaves like a switched-off module (no toast
    // per poll), the caller still sees the rejection.
    const moduleOff =
      r.status === 403 &&
      json.error &&
      typeof json.error === 'object' &&
      json.error.reason === 'download';
    if (!json.toast && !silent && !moduleOff) toast.error(errMsg);
    throw new Error(errMsg);
  }
  // Most HTTP handlers use the ApiResponse envelope ({ ok, data }). The
  // torrent bridge intentionally forwards daemon replies ({ ok, torrents },
  // { ok, torrent }, …) without wrapping them again. Preserve both contracts.
  return Object.prototype.hasOwnProperty.call(json, 'data') ? json.data : json;
}

/** POST shorthand */
export const apiPost = (endpoint, body) => api(endpoint, { method: 'POST', body });
